import { colors, COLORS } from '@constants/colors';

// The walk's own rows in the Scene tree. Everything that can lean on the tree's existing vocabulary
// does — `node`, `nodeSelected`, `nodeLabel`, `groupCount`, `addButton` all come from
// SceneTree/styles.ts — so a segment row is a tree row and reads as one. Only the few things the
// tree has no word for are here.
export const useLayoutTreeStyles = () => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },

  // The entrance's icon, in the lilac the editor uses for "this one is different": it is a placement
  // like any other and it is still not a link in the chain.
  entranceIcon: {
    color: '#AEB6FF',
  },

  // The drag grip on a segment row. A texture rather than an icon, and deliberately inert: the rows
  // reorder from the segment panel, where there is width for a labelled control. The grip is there
  // because a row in an ordered list should look like part of an ordered list.
  grip: {
    width: '10px',
    height: '16px',
    flexShrink: 0,
    borderRadius: '2px',
    backgroundImage:
      'radial-gradient(circle, rgba(255, 255, 255, 0.28) 1px, transparent 1px)',
    backgroundSize: '4px 4px',
    backgroundPosition: '1px 2px',
  },

  rowRefused: {
    fontSize: 14,
    color: '#FF6B6B',
    flexShrink: 0,
  },

  // The "not in this build" marker, rowRefused in everything but colour. Amber rather than red
  // because it is not a refusal the author caused and not one they can move their way out of: the
  // room belongs to some other build's kit, and the only fix is to swap it (MV-2004).
  rowMissing: {
    fontSize: 14,
    color: '#F5A623',
    flexShrink: 0,
  },

  // The end wall. Not selectable and not hoverable, because there is nothing to open for it.
  fixedNode: {
    cursor: 'default',
    color: colors.white_40,
    '&:hover': {
      backgroundColor: 'transparent',
      color: colors.white_40,
    },
  },
  fixedIcon: {
    color: colors.white_30,
  },

  starters: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginTop: '10px',
  },
  startersLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '2px 0 2px',
  },
  startersRule: {
    flex: 1,
    height: '1px',
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
  },
  startersLabelText: {
    color: colors.white_40,
    fontSize: '10px',
    fontWeight: 600,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  },
  starter: {
    display: 'flex',
    alignItems: 'center',
    gap: '9px',
    padding: '7px 8px',
    borderRadius: '9px',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    backgroundColor: 'rgba(255, 255, 255, 0.025)',
    cursor: 'pointer',
    transition:
      'border-color 0.15s ease, background-color 0.15s ease, transform 0.15s ease',
    '&:hover': {
      borderColor: 'rgba(174, 182, 255, 0.45)',
      backgroundColor: 'rgba(69, 84, 229, 0.10)',
      transform: 'translateX(2px)',
    },
  },
  starterGlyph: {
    width: '40px',
    height: '30px',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '5px',
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
  },
  starterText: {
    minWidth: 0,
    flex: 1,
  },
  starterName: {
    color: COLORS.WHITE,
    fontSize: '12.5px',
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  starterHint: {
    color: colors.white_50,
    fontSize: '10.5px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  changeSelected: {
    marginTop: '6px',
    justifyContent: 'flex-start',
    gap: '6px',
    color: colors.white_60,
    fontSize: '12px',
    fontWeight: 500,
    textTransform: 'none',
    padding: '4px 8px',
    '&:hover': {
      color: COLORS.WHITE,
      backgroundColor: 'rgba(255, 255, 255, 0.04)',
    },
  },
  changeSelectedIcon: {
    fontSize: 15,
  },
});
